import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Divider, Input, Tag, Tooltip } from 'tdesign-react';

import { BotChatSelf, BotEysShow, PlayDub } from '@/components/icon';
import { NovaImage } from '@/components/Image';
import { ImageRatio } from '@/const/richMedia';
import { isAuthor } from '@/editor/flowEditor/control/utils/disabled';
import { IPlayerInfo, IStory } from '@/interface';
import { Voice } from '@/interface/aigc';
import { RichMediaValue } from '@/interface/media';
import AigcVoiceDialog from '@/material/AigcVoiceDialog';
import UploadPic from '@/material/form-upload/image';
import { isUgc, isWoa } from '@/utils/getApiDomain';

import styles from './index.module.less';

export interface IProps {
  onChange: (key: any) => void;
  value: IPlayerInfo;
  disabled?: boolean;
  cparams?: { storyInfo?: IStory }
}

const CustomPlayInfo: React.FC<IProps> = (props) => {
  const {
    onChange,
    value,
    disabled = false,
  } = props;
  const { name = '', avatar = {} } = value || {};

  const [searchParams] = useSearchParams();
  const release = searchParams.get('release') === 'true';

  const storyInfo = props?.cparams?.storyInfo;

  const [hideBotIds, setHideBotIds] = React.useState<string[]>([]);
  const [chatBotIds, setChatBotIds] = React.useState<string[]>([]);

  const [visible, setVisible] = useState(false);
  const [systemVoice, setSystemVoice] = useState<Voice | undefined>();
  const systemError = useMemo(() => {
    if (storyInfo?.isOpenDub && !systemVoice?.voiceId) {
      return '请选择音色';
    }
    return '';
  }, [systemVoice, storyInfo?.isOpenDub]);

  useEffect(() => {
    setHideBotIds(storyInfo?.hiddenBotIds || []);
  }, [storyInfo?.hiddenBotIds]);

  useEffect(() => {
    setChatBotIds(storyInfo?.chatBotIds || []);
  }, [storyInfo?.chatBotIds]);

  useEffect(() => {
    if (storyInfo?.narrationDub?.voiceId) {
      setSystemVoice(storyInfo?.narrationDub);
    }
  }, [storyInfo?.narrationDub]);

  const handleClose = () => {
    setVisible(false);
  };

  const handleOpenVoice = () => {
    if (disabled) {
      return;
    }
    setVisible(true);
  };

  const handleConfirm = (src?: Voice) => {
    if (src?.voiceId) {
      setSystemVoice(src);
      onChange({ ...value, narrationDub: src });
    } else {
      setSystemVoice(undefined);
      onChange({ ...value, narrationDub: {} });
    }
    handleClose();
  };

  const handleImageChange = (data?: RichMediaValue) => {
    onChange({ ...value, avatar: data });
  };

  const handleInputChange = (data: string) => {
    onChange({ ...value, name: data });
  };

  const handleBaned = (id?: string) => {
    if (disabled) {
      return;
    }
    if (id) {
      const newHideBotIds = [...hideBotIds];
      const index = newHideBotIds?.indexOf(id);
      // 切换id的存在状态：不存在则添加，存在则移除
      if (index === -1) {
        newHideBotIds.push(id);
      } else {
        newHideBotIds.splice(index, 1);
      }

      // 更新状态并触发回调
      setHideBotIds(newHideBotIds);
      onChange({ ...value, chatBotIds, hiddenBotIds: newHideBotIds });
    }
  };

  const handleOpenChat = (id?: string) => {
    if (disabled && isUgc) {
      return;
    }
    if (disabled && isWoa && !release) {
      // woa 正式表可以修改， 草稿表不可以
      return;
    }
    if (id) {
      const ids = [...chatBotIds];
      const index = ids?.indexOf(id);
      if (index === -1) {
        ids.push(id); // 如果不存在则添加
      } else {
        ids.splice(index, 1); // 如果存在则移除
      }
      setChatBotIds(ids);
      onChange({ ...value, hiddenBotIds: hideBotIds, chatBotIds: ids });
    }
  };

  const errors = useMemo(() => {
    if (!name || name?.trim() === '') {
      return '请补充玩家昵称';
    }
    if (!avatar?.url?.url) {
      return '请补充玩家头像';
    }
    return '';
  }, [name, avatar]);


  const width = avatar?.url?.url ? 80 : 120;

  const chatDisabled = (disabled && isUgc) || (disabled && isWoa && !release);

  return (
    <div className={classNames('custom-form-group', styles.group)}>
      <div className={styles.item}>
        <div className={styles.upload} style={{ width }}>
          <UploadPic
            value={avatar}
            disabled={disabled}
            onChange={handleImageChange}
            width={width}
            height={width}
            wrapStyle={{
              borderRadius: width / 2,
            }}
            imageStyle={{
              width,
            }}
            limit={{ ratio: ImageRatio['1:1'] }}
          />
          <Tag className={classNames(styles.tag, styles.player)}>玩家</Tag>
        </div>
        <Input
          align="center"
          status="default"
          type="text"
          value={name}
          onChange={handleInputChange}
          className={styles.inputWrap}
          disabled={disabled}
          placeholder="输入自定义身份"
          style={{ width: 100, marginTop: 10 }}
        />
        {errors ? <div className={styles.errors}>{errors}</div> : null}
      </div>
      {
        storyInfo?.botPromptList?.map((e, i) => {
          if (!e?.id) {
            return null;
          }
          const baned = (hideBotIds?.length > 0 && (hideBotIds.includes(e?.id)));
          const openChat = (chatBotIds?.length > 0 && (chatBotIds.includes(e?.id)));
          return (
            <div className={styles.item} key={i}>
              <div className={classNames(styles.avatar)}>
                <NovaImage src={e?.avatar} style={{ width: 80, height: 80, borderRadius: 40 }} error="无" />
                {
                  isAuthor(e?.createdBy || '') ? <Tag className={classNames(styles.tag, styles.original)}>原创</Tag> : null
                }
                <div className={classNames(styles.optionArea)}>
                  <Tooltip content="是否展示在首页" placement="top" showArrow destroyOnClose>
                    <div onClick={() => handleBaned(e?.id)} className={classNames(
                      styles.avaIcon, styles.eyes, { [styles.notShowIndex]: baned },
                      { [styles.disabled]: disabled },
                    )}>
                      <BotEysShow />
                    </div>
                  </Tooltip>
                  <Tooltip content="是否打开私聊" placement="top" showArrow destroyOnClose>
                    <div onClick={() => handleOpenChat(e?.id)} className={classNames(
                      styles.avaIcon, styles.chat, { [styles.openChat]: openChat },
                      { [styles.disabled]: chatDisabled },
                    )}>
                      <BotChatSelf />
                    </div>
                  </Tooltip>
                </div>
              </div>
              <div
                className={styles.input}
                style={{ width: 100, marginTop: 10, textAlign: 'center' }}
              >{e?.name || '未命名'}</div>
            </div>
          );
        })
      }
      <Divider layout="vertical" style={{ height: 100 }}></Divider>

      <div className={classNames(styles.item, styles.system)}>
        <div className={
          classNames(styles.uploadSystem, { [styles.disabled]: disabled })
        } onClick={handleOpenVoice}>
          <div className={styles.systemImageWrap}>
            <NovaImage src={{
              url: {
                url: 'https://nova.file.video.qpic.cn/uploadFile/300000000000119/ea36e7347ebb95487e30d83b65b0d112609699136af070ecaaf00158ba787d4f.png',
              },
            }} error="无" className={styles.systemImage} />
          </div>
          <Tag className={classNames(styles.tag, styles.player)}>旁白</Tag>
          <div className={classNames(styles.optionArea)}>
            <PlayDub />
            {
              systemVoice?.voiceName && systemVoice?.voiceId
                ? <div className={styles.voiceIdName}>{systemVoice?.voiceName}</div>
                : null
            }
          </div>
        </div>
        <div
          className={styles.input}
          style={{ width: 100, marginTop: 10, textAlign: 'center' }}
        >{'旁白'}</div>
        {systemError ? <div className={classNames(styles.errors, styles.systemError)}>{systemError}</div> : null}
      </div>

      <AigcVoiceDialog
        data={systemVoice}
        visible={visible}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default CustomPlayInfo;
